import { useAuth } from "@/_core/hooks/useAuth";
import { getLoginUrl } from "@/const";
import { trpc } from "@/lib/trpc";
import { ShieldX } from "lucide-react";
import { useEffect, useState } from "react";

export default function RequireOperationsAccess({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading, logout } = useAuth();
  const [signedOutLoadTimedOut, setSignedOutLoadTimedOut] = useState(false);
  const access = trpc.operations.access.useQuery(undefined, {
    enabled: Boolean(user),
    retry: false,
  });

  useEffect(() => {
    if (user || !authLoading) {
      setSignedOutLoadTimedOut(false);
      return;
    }
    const timeout = window.setTimeout(() => setSignedOutLoadTimedOut(true), 2_500);
    return () => window.clearTimeout(timeout);
  }, [authLoading, user]);

  if ((authLoading && !signedOutLoadTimedOut) || (Boolean(user) && access.isLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fffdf5]">
        <div className="text-center">
          <div className="h-8 w-8 rounded-full border-2 border-[#3057bf] border-t-transparent animate-spin mx-auto" />
          <p className="mt-4 text-sm text-[#112451]/55">Checking private Operations Hub access…</p>
        </div>
      </div>
    );
  }

  if (!user || access.isError || !access.data?.allowed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fffdf5] px-5">
        <div className="max-w-md rounded-3xl bg-white p-9 text-center shadow-xl shadow-[#112451]/10 border border-[#112451]/5">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
            <ShieldX className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-[#112451]">Private access only</h1>
          <p className="mt-3 text-sm leading-6 text-[#112451]/65">
            The Operations Hub contains confidential administrative information and is restricted to the designated Assist administrators.
          </p>
          <p className="mt-4 text-xs text-[#112451]/45">{user ? `Signed in as ${user.email || user.name || "Unknown"}` : "Please sign in with an approved Assist administrator account."}</p>
          {user ? (
            <button onClick={logout} className="mt-7 rounded-xl bg-[#112451] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#0c1b42]">
              Sign out
            </button>
          ) : (
            <a href={getLoginUrl()} className="mt-7 inline-flex rounded-xl bg-[#112451] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#0c1b42]">
              Sign in
            </a>
          )}
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
